// File: src/lib/graphql/queries/project.ts
// Purpose: Project Management GraphQL Queries and Mutations
// VIBE - Project API

import { gql } from 'graphql-request'
import { executeGraphQL } from '../client'

// ============================================
// TYPES
// ============================================

export interface Project {
  _id: string
  id?: string  // For backward compatibility
  name: string
  code: string
  description?: string
  projectType?: {
    _id: string
    title: string
    is_active: boolean
  }
  status: string
  startDate: string
  endDate?: string
  actualStartDate?: string
  actualEndDate?: string
  budget?: {
    estimated?: number
    actual?: number
    currency?: string
  }
  progress: number
  priority: string
  tags?: string[]
  organization?: {
    _id: string
    name: string
    code: string
    legalName?: string
    registrationNumber?: string
    taxId?: string
    website?: string
    email?: string
    phone?: string
    logo?: string
    foundedDate?: string
    employeeCount?: number
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
  projectManager?: {
    _id: string
    firstName: string
    lastName: string
    email: string
    azureId?: string
    azureTenantId?: string
    azureEmail?: string
    azureDisplayName?: string
    ssoEnabled?: boolean
    lastAzureLogin?: string
    azureGroups?: string[]
    countryCode?: string
    mobile_number?: string
    languages?: string[]
    joining_date?: string
    skills?: string[]
    designation?: string
    department?: string
    shift?: string
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
  client?: {
    name: string
    email: string
    phone: string
    company: string
  }
  department?: {
    _id: string
    name: string
    code: string
    description?: string
    employeeCount?: number
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
  teamMembers?: TeamMember[]
  milestones?: Milestone[]
  tasks?: Task[]
  is_active: boolean
  is_deleted?: boolean
  createdAt: string
  updatedAt: string
}

export interface ProjectType {
  _id: string
  id?: string  // For backward compatibility
  title: string
  name?: string  // For backward compatibility
  code?: string
  description?: string
  is_active: boolean
  isActive?: boolean  // For backward compatibility
}

export type ProjectStatus = 'IN_PROGRESS' | 'PLANNING' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'

export interface TeamMember {
  _id: string
  joinedDate: string
  allocation: number
  projectId: string
  title?: string
  createdAt: string
  updatedAt: string
  teamMembers: {
    joinedDate: string
    allocation: number
    employee: {
      _id: string
      firstName: string
      email: string
      lastName: string
      role: {
        _id: string
        title: string
        is_active: boolean
      }
      countryCode?: string
      mobile_number?: string
      joining_date?: string
      skills?: string[]
      designation?: string
      department?: string
      is_active: boolean
      createdAt: string
      updatedAt: string
    }
  }[]
}

export interface Milestone {
  _id: string
  id?: string  // For backward compatibility
  name: string
  description?: string
  dueDate?: string
  completedDate?: string
  isCompleted: boolean
  deliverables?: string[]
}

export interface Task {
  _id: string
  id?: string  // For backward compatibility
  title: string
  description?: string
  status: string
  priority: string
  assignee?: {
    _id: string
    firstName: string
    lastName: string
  }
  dueDate?: string
  completedDate?: string
}

export interface ProjectDocument {
  id: string
  originalName: string
  storedName: string
  mimeType: string
  extension: string
  size: number
  path: string
  url: string
  isDeleted: boolean
  uploadedBy: string
  createdAt: string
  updatedAt: string
  processingStatus: string
  separatedFiles?: {
    id: string
    type: string
    storedName: string
    url: string
    path: string
    size: number
  }[]
  organizationMetadata?: {
    category: string
    categoryPath: string
    version: string
    contentHash: string
    baseName: string
    isDuplicate: boolean
    relatedFiles: string[]
    tags: string[]
  }
  aiProcessing?: {
    extractedText: string
    processedAt: string
    processingTime: number
    error: string
  }
}

export interface ProjectDocuments {
  id: string
  projectId: string
  title: string
  createdAt: string
  updatedAt: string
  documents: ProjectDocument[]
}

export interface CreateProjectDocumentInput {
  projectId: string
  title: string
  documents: {
    originalName: string
    storedName: string
    mimeType: string
    extension: string
    size: number
    path: string
    url: string
  }[]
}

export interface ProcessDocumentResponse {
  success: boolean
  message: string
  separatedFiles: {
    type: string
    url: string
  }[]
  categories: string[]
}

export interface TeamMemberInput {
  employee: string // Employee ID
  role: string // Role ID
  allocation: number // Allocation percentage
  joinedDate?: string // Joined date
}

export interface AddMemberToProjectTeamResponse {
  _id: string
  joinedDate: string
  allocation: number
  projectId: string
  title: string
  createdAt: string
  updatedAt: string
  employee: {
    _id: string
    firstName: string
    lastName: string
    email: string
    skills?: string[]
    joining_date?: string
    languages?: string[]
    mobile_number?: string
    designation?: string
    countryCode?: string
    department?: string
    role: {
      _id: string
      title: string
      is_active: boolean
    }
  }
  role: {
    _id: string
    title: string
    is_active: boolean
  }
  teamMembers: {
    joinedDate: string
    allocation: number
    role: {
      _id: string
      title: string
      is_active: boolean
    }
    employee: {
      _id: string
      firstName: string
      lastName: string
      email: string
      countryCode?: string
      mobile_number?: string
      languages?: string[]
      joining_date?: string
      skills?: string[]
      designation?: string
      role: {
        _id: string
        title: string
        is_active: boolean
      }
      is_active: boolean
    }
  }[]
}

export interface UpdateProjectInput {
  _id: string
  name?: string
  code?: string
  description?: string
  organization?: string
  projectType?: string
  client?: {
    name?: string
    email?: string
    phone?: string
    company?: string
  }
  projectManager?: string
  department?: string
  status?: string
  priority?: string
  startDate?: string
  endDate?: string
  actualStartDate?: string
  actualEndDate?: string
  budget?: {
    estimated?: number
    currency?: string
  }
  tags?: string[]
  progress?: number
  title?: string
  is_active?: boolean
}

export interface AddMilestoneInput {
  projectId: string
  name: string
  description?: string
  dueDate?: string | null
}

export interface UpdateMilestoneInput {
  projectId: string
  milestoneId: string
  name?: string
  dueDate?: string | null
  description?: string
  isCompleted?: boolean
}

// ============================================
// QUERIES
// ============================================

export const PROJECTS_QUERY = gql`
  query Projects(
    $cursor: String,
    $organization: String,
    $limit: Int,
    $status: ProjectStatus,
    $projectManager: String,
    $department: String
  ) {
    projects(
      cursor: $cursor,
      organization: $organization,
      limit: $limit,
      status: $status,
      projectManager: $projectManager,
      department: $department
    ) {
      nextCursor
      edges {
        _id
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        is_deleted
        createdAt
        updatedAt
        organization {
          _id
          name
          code
          legalName
          registrationNumber
          taxId
          website
          email
          phone
          logo
          foundedDate
          employeeCount
          is_active
          is_deleted
          createdAt
          updatedAt
        }
        department {
          _id
          name
          code
          description
          employeeCount
          is_active
          is_deleted
          createdAt
          updatedAt
        }
        projectManager {
          azureId
          azureTenantId
          azureEmail
          azureDisplayName
          ssoEnabled
          lastAzureLogin
          azureGroups
          _id
          firstName
          lastName
          email
          countryCode
          mobile_number
          languages
          joining_date
          skills
          designation
          department
          shift
          is_active
          is_deleted
          createdAt
          updatedAt
        }
        client {
          name
          email
          phone
          company
        }
        projectType {
          _id
          title
          is_active
        }
        budget {
          estimated
          actual
          currency
        }
        milestones {
          _id
          name
          description
          dueDate
          completedDate
          isCompleted
        }
      }
    }
  }
`

export const PROJECT_QUERY = gql`
  query Project($id: ID!) {
    project(id: $id) {
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        createdAt
        updatedAt
        _id
        projectType {
            _id
            title
            is_active
        }
        budget {
            estimated
            actual
            currency
        }
        organization {
            _id
            code
            name
        }
        projectManager {
            _id
            firstName
            lastName
            email
        }
        milestones {
            _id
            name
            description
            completedDate
            dueDate
            isCompleted
        }
    }
  }
`

export const PROJECT_BY_CODE_QUERY = gql`
  query ProjectByCode($code: String!) {
    projectByCode(code: $code) {
      id
      name
      code
      description
      status
      progress
      priority
      isActive
    }
  }
`

export const PROJECTS_BY_EMPLOYEE_QUERY = gql`
  query ProjectsByEmployee($employeeId: ID!, $cursor: String, $limit: Int) {
    projectsByEmployee(employeeId: $employeeId, cursor: $cursor, limit: $limit) {
      nextCursor
      edges {
        _id
        name
        code
        description
        status
        priority
        startDate
        endDate
        actualStartDate
        actualEndDate
        tags
        progress
        is_active
        is_deleted
        createdAt
        updatedAt
      }
    }
  }
`

export const PROJECT_TYPES_QUERY = gql`
  query ProjectTypes($first: Int) {
    projectTypes(limit: $first) {
      nextCursor
      edges {
          _id
          title
          is_active
      }
    }
  }
`

export const TEAM_MEMBERS_BY_PROJECT_QUERY = gql`
  query TeamMembersByProject($projectId: ID!) {
    teamMembersByProject(projectId: $projectId) {
      _id
      joinedDate
      allocation
      projectId
      title
      createdAt
      updatedAt
      teamMembers {
        joinedDate
        allocation
        employee {
          _id
          firstName
          email
          lastName
          role {
            _id
            title
            is_active
          }
          countryCode
          mobile_number
          joining_date
          skills
          designation
          department
          is_active
          createdAt
          updatedAt
        }
      }
    }
  }
`

export const PROJECT_DOCUMENTS_BY_PROJECT_QUERY = gql`
  query ProjectDocumentsByProject($projectId: ID!) {
    projectDocumentsByProject(projectId: $projectId) {
      id
      projectId
      title
      createdAt
      updatedAt
      documents {
        id
        originalName
        storedName
        mimeType
        extension
        size
        path
        url
        isDeleted
        uploadedBy
        createdAt
        updatedAt
        processingStatus
        separatedFiles {
          id
          type
          storedName
          url
          path
          size
        }
        organizationMetadata {
          category
          categoryPath
          version
          contentHash
          baseName
          isDuplicate
          relatedFiles
          tags
        }
        aiProcessing {
          extractedText
          processedAt
          processingTime
          error
        }
      }
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const CREATE_PROJECT_MUTATION = gql`
  mutation CreateProject(
    $name: String!,
    $code: String!,
    $description: String,
    $organization: ID,
    $projectType: ID,
    $status: ProjectStatus,
    $priority: ProjectPriority,
    $startDate: String,
    $endDate: String,
    $estimatedBudget: Float,
    $currency: String
  ) {
    createProject(
      input: {
        name: $name
        code: $code
        description: $description
        organization: $organization
        projectType: $projectType
        status: $status
        priority: $priority
        startDate: $startDate
        endDate: $endDate
        budget: { estimated: $estimatedBudget, currency: $currency }
      }
    ) {
      _id
      name
      code
      description
      status
      priority
      startDate
      endDate
      actualStartDate
      actualEndDate
      tags
      progress
      is_active
      is_deleted
      createdAt
      updatedAt
      projectType {
        _id
        title
        is_active
      }
    }
  }
`

export const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($input: UpdateProjectInput!) {
    updateProject(input: $input) {
      _id
      name
      code
      description
      status
      priority
      startDate
      endDate
      actualStartDate
      actualEndDate
      tags
      progress
      is_active
      is_deleted
      createdAt
      updatedAt
      budget {
        estimated
        actual
        currency
      }
    }
  }
`

export const DELETE_PROJECT_MUTATION = gql`
  mutation DeleteProject($id: ID!) {
    deleteProject(id: $id) {
      success
      message
    }
  }
`

export const UPDATE_PROJECT_STATUS_MUTATION = gql`
  mutation UpdateProjectStatus($id: ID!, $status: ProjectStatus!) {
    updateProjectStatus(id: $id, status: $status) {
      id
      status
      updatedAt
    }
  }
`

export const ADD_PROJECT_MILESTONE_MUTATION = gql`
  mutation AddProjectMilestone($input: AddMilestoneInput!) {
    addProjectMilestone(input: $input) {
      _id
      name
      code
      description
      status
      priority
      startDate
      endDate
      actualStartDate
      actualEndDate
      tags
      progress
      is_active
      is_deleted
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_PROJECT_MILESTONE_MUTATION = gql`
  mutation UpdateProjectMilestone($input: UpdateMilestoneInput!) {
    updateProjectMilestone(input: $input) {
      _id
      name
      code
      description
      status
      priority
      startDate
      endDate
      actualStartDate
      actualEndDate
      tags
      progress
      is_active
      is_deleted
      createdAt
      updatedAt
    }
  }
`

export const ADD_MEMBER_TO_PROJECT_TEAM_MUTATION = gql`
  mutation AddMemberToProjectTeam($projectId: ID!, $teamMember: TeamMemberAssignmentInput!, $title: String!) {
    addMemberToProjectTeam(projectId: $projectId, teamMember: $teamMember, title: $title) {
      _id
      joinedDate
      allocation
      projectId
      title
      createdAt
      updatedAt
      employee {
        _id
        firstName
        lastName
        email
        skills
        joining_date
        languages
        mobile_number
        designation
        countryCode
        department
        role {
          _id
          title
          is_active
        }
      }
      role {
        _id
        title
        is_active
      }
      teamMembers {
        joinedDate
        allocation
        employee {
          _id
          firstName
          lastName
          email
          countryCode
          mobile_number
          languages
          joining_date
          skills
          designation
          is_active
        }
      }
    }
  }
`

export const REMOVE_TEAM_MEMBER_MUTATION = gql`
  mutation RemoveTeamMember($projectId: ID!, $employeeId: ID!) {
    removeTeamMember(projectId: $projectId, employeeId: $employeeId) {
      _id
      name
      code
      description
      status
      priority
      startDate
      endDate
      actualStartDate
      actualEndDate
      tags
      progress
      is_active
      is_deleted
      createdAt
      updatedAt
    }
  }
`

export const UPDATE_TEAM_MEMBER_ROLE_MUTATION = gql`
  mutation UpdateTeamMemberRole($projectId: ID!, $memberId: ID!, $role: String!) {
    updateTeamMemberRole(projectId: $projectId, memberId: $memberId, role: $role) {
      id
      role
    }
  }
`

export const UPDATE_TEAM_MEMBER_ALLOCATION_MUTATION = gql`
  mutation UpdateTeamMemberAllocation($projectId: ID!, $memberId: ID!, $allocation: Float!) {
    updateTeamMemberAllocation(projectId: $projectId, memberId: $memberId, allocation: $allocation) {
      id
      allocation
    }
  }
`

export const CREATE_PROJECT_DOCUMENT_MUTATION = gql`
  mutation CreateProjectDocument($input: CreateProjectDocumentInput!) {
    createProjectDocument(input: $input) {
      id
      projectId
      title
      documents {
        id
        originalName
        storedName
        url
        processingStatus
        size
        mimeType
        createdAt
      }
    }
  }
`

export const PROCESS_DOCUMENT_MUTATION = gql`
  mutation ProcessDocument($projectId: ID!, $fileId: ID!) {
    processAndOrganizeDocument(projectId: $projectId, fileId: $fileId) {
      success
      message
      separatedFiles {
        type
        url
      }
      categories
    }
  }
`

// Helper types for paginated responses
interface ProjectEdge {
  _id: string
  name: string
  code: string
  description?: string
  status: string
  priority: string
  startDate: string
  endDate?: string
  actualStartDate?: string
  actualEndDate?: string
  tags?: string[]
  progress: number
  is_active: boolean
  is_deleted?: boolean
  createdAt: string
  updatedAt: string
  organization?: {
    _id: string
    name: string
    code: string
    legalName?: string
    registrationNumber?: string
    taxId?: string
    website?: string
    email?: string
    phone?: string
    logo?: string
    foundedDate?: string
    employeeCount?: number
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
  department?: {
    _id: string
    name: string
    code: string
    description?: string
    employeeCount?: number
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
  projectManager?: {
    azureId?: string
    azureTenantId?: string
    azureEmail?: string
    azureDisplayName?: string
    ssoEnabled?: boolean
    lastAzureLogin?: string
    azureGroups?: string[]
    _id: string
    firstName: string
    lastName: string
    email: string
    countryCode?: string
    mobile_number?: string
    languages?: string[]
    joining_date?: string
    skills?: string[]
    designation?: string
    department?: string
    shift?: string
    is_active: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
  }
  client?: {
    name: string
    email: string
    phone: string
    company: string
  }
  projectType?: {
    _id: string
    title: string
    is_active: boolean
  }
  budget?: {
    estimated?: number
    actual?: number
    currency?: string
  }
  milestones?: {
    _id: string
    name: string
    description?: string
    dueDate?: string
    completedDate?: string
    isCompleted: boolean
  }[]
}

interface ProjectsResponse {
  nextCursor?: string
  edges: ProjectEdge[]
}

interface ProjectsByEmployeeResponse {
  edges: {
    node: ProjectEdge
    cursor: string
  }[]
  pageInfo: {
    hasNextPage: boolean
    endCursor?: string
  }
  totalCount: number
}

interface ProjectTypesResponse {
  nextCursor?: string
  edges: ProjectType[]
}

interface RemoveTeamMemberResponse {
  _id: string
  name: string
  code: string
  description?: string
  status: string
  priority: string
  startDate: string
  endDate?: string
  actualStartDate?: string
  actualEndDate?: string
  tags?: string[]
  progress: number
  is_active: boolean
  is_deleted?: boolean
  createdAt: string
  updatedAt: string
}

interface DeleteProjectResponse {
  success: boolean
  message: string
}

// ============================================
// API SERVICE CLASS
// ============================================

export class ProjectAPI {
  // Project Queries - Backward compatible with overloaded signatures
  static async getProjects(first?: number, after?: string, filter?: Record<string, unknown>): Promise<ProjectsResponse>
  static async getProjects(options?: {
    cursor?: string,
    organization?: string,
    limit?: number,
    status?: ProjectStatus,
    projectManager?: string,
    department?: string
  }): Promise<ProjectsResponse>
  static async getProjects(
    firstOrOptions?: number | {
      cursor?: string,
      organization?: string,
      limit?: number,
      status?: ProjectStatus,
      projectManager?: string,
      department?: string
    },
    after?: string,
    filter?: Record<string, unknown>
  ): Promise<ProjectsResponse> {
    let queryParams: any = {}

    // Handle backward compatibility - if first parameter is a number, use old signature
    if (typeof firstOrOptions === 'number' || firstOrOptions === undefined) {
      queryParams = {
        limit: firstOrOptions || 10,
        cursor: after,
        ...filter
      }
    } else {
      // New options object approach
      queryParams = {
        cursor: firstOrOptions.cursor,
        organization: firstOrOptions.organization,
        limit: firstOrOptions.limit || 10,
        status: firstOrOptions.status,
        projectManager: firstOrOptions.projectManager,
        department: firstOrOptions.department
      }
    }

    const { projects } = await executeGraphQL<{ projects: ProjectsResponse }>(
      PROJECTS_QUERY,
      queryParams
    )
    return projects
  }

  static async getProject(id: string): Promise<Project> {
    const { project } = await executeGraphQL<{ project: Project }>(
      PROJECT_QUERY,
      { id }
    )
    return project
  }

  static async getProjectByCode(code: string): Promise<Project> {
    const { projectByCode } = await executeGraphQL<{ projectByCode: Project }>(
      PROJECT_BY_CODE_QUERY,
      { code }
    )
    return projectByCode
  }

  static async getProjectsByEmployee(employeeId: string, limit?: number, cursor?: string): Promise<any> {
    const { projectsByEmployee } = await executeGraphQL<{ projectsByEmployee: any }>(
      PROJECTS_BY_EMPLOYEE_QUERY,
      { employeeId, cursor, limit }
    )
    return projectsByEmployee
  }

  static async getProjectTypes(first: number = 10, after?: string): Promise<ProjectTypesResponse> {
    const { projectTypes } = await executeGraphQL<{ projectTypes: ProjectTypesResponse }>(
      PROJECT_TYPES_QUERY,
      { first, after }
    )
    return projectTypes
  }

  static async getTeamMembersByProject(projectId: string): Promise<TeamMember[]> {
    const { teamMembersByProject } = await executeGraphQL<{ teamMembersByProject: TeamMember[] }>(
      TEAM_MEMBERS_BY_PROJECT_QUERY,
      { projectId }
    )
    return teamMembersByProject
  }

  static async getProjectDocumentsByProject(projectId: string): Promise<ProjectDocuments[]> {
    const { projectDocumentsByProject } = await executeGraphQL<{ projectDocumentsByProject: ProjectDocuments[] }>(
      PROJECT_DOCUMENTS_BY_PROJECT_QUERY,
      { projectId }
    )
    return projectDocumentsByProject
  }

  // Project Mutations
  static async createProject(projectData: {
    name?: string;
    code?: string;
    description?: string;
    organization?: string;
    projectType?: string;
    status?: string;
    priority?: string;
    startDate?: string;
    endDate?: string;
    estimatedBudget?: number;
    currency?: string;
  }): Promise<Project> {
    const { createProject } = await executeGraphQL<{ createProject: Project }>(
      CREATE_PROJECT_MUTATION,
      {
        name: projectData.name,
        code: projectData.code,
        description: projectData.description,
        organization: projectData.organization,
        projectType: projectData.projectType,
        status: projectData.status?.toUpperCase(),
        priority: projectData.priority?.toUpperCase(),
        startDate: projectData.startDate,
        endDate: projectData.endDate,
        estimatedBudget: projectData.estimatedBudget,
        currency: projectData.currency
      }
    )
    return createProject
  }

  static async updateProject(input: UpdateProjectInput): Promise<Project> {
    const { updateProject } = await executeGraphQL<{ updateProject: Project }>(
      UPDATE_PROJECT_MUTATION,
      { input }
    )
    return updateProject
  }

  static async deleteProject(id: string): Promise<DeleteProjectResponse> {
    const { deleteProject } = await executeGraphQL<{ deleteProject: DeleteProjectResponse }>(
      DELETE_PROJECT_MUTATION,
      { id }
    )
    return deleteProject
  }

  static async updateProjectStatus(id: string, status: ProjectStatus): Promise<Project> {
    const { updateProjectStatus } = await executeGraphQL<{ updateProjectStatus: Project }>(
      UPDATE_PROJECT_STATUS_MUTATION,
      { id, status }
    )
    return updateProjectStatus
  }

  // Milestone Management
  static async addProjectMilestone(input: AddMilestoneInput): Promise<Project> {
    const { addProjectMilestone } = await executeGraphQL<{ addProjectMilestone: Project }>(
      ADD_PROJECT_MILESTONE_MUTATION,
      { input }
    )
    return addProjectMilestone
  }

  static async updateProjectMilestone(input: UpdateMilestoneInput): Promise<Project> {
    const { updateProjectMilestone } = await executeGraphQL<{ updateProjectMilestone: Project }>(
      UPDATE_PROJECT_MILESTONE_MUTATION,
      { input }
    )
    return updateProjectMilestone
  }

  // Team Management
  static async addMemberToProjectTeam(projectId: string, teamMember: TeamMemberInput, title: string): Promise<AddMemberToProjectTeamResponse> {
    const { addMemberToProjectTeam } = await executeGraphQL<{ addMemberToProjectTeam: AddMemberToProjectTeamResponse }>(
      ADD_MEMBER_TO_PROJECT_TEAM_MUTATION,
      { projectId, teamMember, title }
    )
    return addMemberToProjectTeam
  }

  // Backward compatibility method
  static async addTeamMember(projectId: string, member: TeamMemberInput & { title?: string }): Promise<AddMemberToProjectTeamResponse> {
    const { title, ...teamMember } = member
    return this.addMemberToProjectTeam(projectId, {
      ...teamMember,
      joinedDate: teamMember.joinedDate || new Date().toISOString()
    }, title || 'Default Team')
  }

  static async removeTeamMember(projectId: string, employeeId: string): Promise<RemoveTeamMemberResponse> {
    const { removeTeamMember } = await executeGraphQL<{ removeTeamMember: RemoveTeamMemberResponse }>(
      REMOVE_TEAM_MEMBER_MUTATION,
      { projectId, employeeId }
    )
    return removeTeamMember
  }

  static async updateTeamMemberRole(projectId: string, memberId: string, role: string): Promise<TeamMember> {
    const { updateTeamMemberRole } = await executeGraphQL<{ updateTeamMemberRole: TeamMember }>(
      UPDATE_TEAM_MEMBER_ROLE_MUTATION,
      { projectId, memberId, role }
    )
    return updateTeamMemberRole
  }

  static async updateTeamMemberAllocation(projectId: string, memberId: string, allocation: number): Promise<TeamMember> {
    const { updateTeamMemberAllocation } = await executeGraphQL<{ updateTeamMemberAllocation: TeamMember }>(
      UPDATE_TEAM_MEMBER_ALLOCATION_MUTATION,
      { projectId, memberId, allocation }
    )
    return updateTeamMemberAllocation
  }

  // Document Management
  static async createProjectDocument(input: CreateProjectDocumentInput): Promise<ProjectDocuments> {
    const { createProjectDocument } = await executeGraphQL<{ createProjectDocument: ProjectDocuments }>(
      CREATE_PROJECT_DOCUMENT_MUTATION,
      { input }
    )
    return createProjectDocument
  }

  static async processAndOrganizeDocument(projectId: string, fileId: string): Promise<ProcessDocumentResponse> {
    const { processAndOrganizeDocument } = await executeGraphQL<{ processAndOrganizeDocument: ProcessDocumentResponse }>(
      PROCESS_DOCUMENT_MUTATION,
      { projectId, fileId }
    )
    return processAndOrganizeDocument
  }
}